import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Row, Col, Typography, Button, Result, Empty } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';

import { SiteHeader, SiteFooter } from './RematePage';
// LAS DOS JUNTAS. `AnimalCard` no se dibuja sola: sin `ESTILOS_CARD` pierde el
// radio, la sombra y el `overflow: hidden` que recorta la foto contra las
// esquinas — se veia asi en este listado hasta que se agrego la hoja.
import { AnimalCard, ESTILOS_CARD } from './CatalogoPage';
import { tipografiaDe, altoHeaderDe } from '../../utils/micrositioTema';
import { linksDelProductor, seccionesDelCatalogo } from '../../utils/menuCatalogo';
import { fixUrl, catalogoPublicoService, analyticsService } from '../../services/api';
import { useAvisarAltoAlPadre } from '../../hooks/useAvisarAltoAlPadre';
import PujaSpinner from '../../components/PujaSpinner';
import BarraBienvenida from './components/BarraBienvenida';
import { useAltoHeaderMedido } from './useAltoHeaderMedido';

const { Title, Text } = Typography;

/**
 * El listado COMPLETO de una sección del catálogo: `/genetica/:slug/:seccion`.
 *
 * La portada muestra los destacados y dice cuántos hay; esta página es el otro
 * lado de esa frase. Hasta que existió, el "Mostrando 6 de 20" no llevaba a
 * ningún lado — y el comentario que lo decía vive en `CatalogoPage`, porque
 * quien lo escribió prefirió no prometer una página que no estaba.
 *
 * NO DEPENDE DE LA PLANTILLA para QUÉ SE MUESTRA: si decidiera eso, cambiarla
 * le rompería al productor las URLs que ya compartió, y en silencio. Sí decide
 * DÓNDE cae la franja de bienvenida (`franjaSuperior`, dentro de la cabecera
 * en OP-1, ausente en OP-2) — igual que en la portada y en la ficha.
 */
const SeccionCatalogoPage = () => {
  // Igual que la portada y la ficha: le avisa al sitio incrustante cuánto
  // medimos, para que agrande el iframe en vez de dejarlo en los 800px del
  // snippet. Y pone `data-embed` en el `<html>`, que es lo que activa las
  // reglas de `index.css` — de ahí la clase en el `<div>` de abajo.
  useAvisarAltoAlPadre();

  const { slug, seccion } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Un 404 y un 500 NO son la misma pantalla, igual que en la portada: el
  // primero es "esta sección no está", el segundo es "volvé a intentar".
  const [error, setError] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    let vivo = true;
    setLoading(true);
    setError(null);
    // Se limpia al cambiar de sección: sin esto, pasar de toros a hembras
    // muestra la lista anterior mientras carga la nueva.
    setData(null);
    (async () => {
      try {
        const res = await catalogoPublicoService.getSeccion(slug, seccion);
        if (vivo) setData(res.data);

        // La visita al listado cuenta como visita del catálogo, con el MISMO
        // tipo que la portada: es una página del catálogo que alguien abrió.
        // catch vacío a propósito — el tracking es fire-and-forget.
        const idCatalogo = res.data?.catalogo?.id_catalogo_genetica;
        if (vivo && idCatalogo) {
          analyticsService.trackCatalogo('visita_index', idCatalogo).catch(() => {});
        }
      } catch (err) {
        if (vivo) {
          setData(null);
          setError(err.response?.status === 404 ? 'not_found' : 'server_error');
        }
      } finally {
        if (vivo) setLoading(false);
      }
    })();
    return () => { vivo = false; };
  }, [slug, seccion]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // El alto REAL de la cabecera, medido — compartido con `CatalogoPage` y
  // `AnimalCatalogoPage`. Antes esta página usaba SOLO el teórico
  // (`altoHeaderDe`), que alcanzaba mientras la cabecera medía lo que el tipo
  // de cabecera predice; desde que la franja de bienvenida de OP-1 vive
  // DENTRO de la cabecera, el teórico solo deja el listado arrancando tapado.
  // Ver el porqué completo en `useAltoHeaderMedido.js`. Se llama ANTES de los
  // early return de abajo: los hooks no pueden ser condicionales.
  const altoHeaderMedido = useAltoHeaderMedido(data);

  const volver = (
    <Button type="primary" onClick={() => navigate(`/genetica/${slug}`)}>
      Volver al catálogo
    </Button>
  );

  if (loading) return <PujaSpinner size="lg" label="Cargando…" fondo="#fff" />;

  if (!data) {
    return error === 'server_error' ? (
      <Result
        status="500"
        title="No pudimos cargar esta página"
        subTitle="Puede ser algo pasajero. Probá de nuevo en un momento."
        extra={volver}
      />
    ) : (
      <Result
        status="404"
        title="No encontramos esta sección"
        subTitle="El enlace puede ser incorrecto, o la cabaña dejó de mostrarla."
        extra={volver}
      />
    );
  }

  const config = data.catalogo.configuracion_micrositio || {};
  const tipografia = tipografiaDe(config);
  // `altoHeaderDe` es el teórico, de respaldo para el primer render antes de
  // que exista algo que medir; `altoHeaderMedido` es el real. Mismo patrón
  // que `CatalogoPage` y `AnimalCatalogoPage`.
  const altoHeader = altoHeaderMedido || altoHeaderDe(config);
  const conCabecera = config.cabecera_on !== false;
  const conPie = config.footer_on !== false;
  // La franja de bienvenida de OP-1 vive DENTRO de la cabecera en las TRES
  // páginas del catálogo — ver el porqué completo en `CatalogoPage.jsx`.
  // `|| 'op1'` es para un catálogo servido por una API vieja: el endpoint de
  // sección manda `plantilla_index` desde el 2026-09-22 (antes solo la
  // portada lo hacía, y un catálogo OP-2 mostraba la franja igual acá).
  const plantilla = data.catalogo.plantilla_index || 'op1';
  const franjaBienvenida = plantilla === 'op1'
    ? <BarraBienvenida config={config} tipografia={tipografia} variante="franja" />
    : null;

  const logoCrudo = config.logo_img || data.identidad?.logo_url || null;
  const logoUrl = logoCrudo ? fixUrl(logoCrudo) : null;
  const comoEvento = { nombre_evento: data.catalogo.nombre };

  // LA MISMA CABECERA QUE LA PORTADA Y LA FICHA. El menú viene armado del
  // backend (`data.menu`) con los mismos animales que muestra la portada: si
  // acá listara todos, el comprador vería dos menús distintos en el mismo sitio.
  const navSecciones = seccionesDelCatalogo(
    data.menu,
    (destino) => navigate(`/genetica/${slug}/animal/${destino}`),
  );
  navSecciones.push(...linksDelProductor(
    config,
    (hash) => navigate(`/genetica/${slug}${hash}`),
  ));
  const navHeader = [
    { key: 'volver', label: 'Volver al catálogo', onClick: () => navigate(`/genetica/${slug}`) },
    ...navSecciones,
  ];

  const animales = data.animales || [];

  return (
    <div className="micrositio-page" style={{ background: '#fff', minHeight: '100vh' }}>
      {/* La hoja de la tarjeta, UNA vez para toda la grilla y no una por
          tarjeta. Mismo criterio que la portada, que ya la inserta asi. */}
      <style>{ESTILOS_CARD}</style>

      {conCabecera && (
        <SiteHeader
          config={config}
          tipografia={tipografia}
          logoUrl={logoUrl}
          evento={comoEvento}
          scrolled={scrolled}
          isAuthenticated={false}
          slug={slug}
          navPropia={navHeader}
          rutaInicio={`/genetica/${slug}`}
          franjaSuperior={franjaBienvenida}
        />
      )}

      <div style={{ paddingTop: conCabecera ? altoHeader : 0 }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '32px 24px 64px' }}>

          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(`/genetica/${slug}`)}
            style={{ fontFamily: tipografia.body, marginBottom: 16, paddingLeft: 0 }}
          >
            Catálogo
          </Button>

          <Title
            level={2}
            style={{
              fontFamily: tipografia.title,
              color: config.color_title || '#1a1a1a',
              margin: 0,
            }}
          >
            {data.seccion.titulo}
          </Title>
          {data.seccion.subtitulo && (
            <Text
              style={{
                fontFamily: tipografia.body,
                color: config.color_paragraph || '#666',
                display: 'block',
                marginTop: 6,
              }}
            >
              {data.seccion.subtitulo}
            </Text>
          )}
          {/* EL TOTAL, dicho una vez. La portada promete "Mostrando 6 de 20" y
              esta pagina es la que cumple; repetir el conteo en cada tarjeta
              seria ruido. */}
          <Text
            type="secondary"
            style={{ fontFamily: tipografia.body, display: 'block', marginTop: 4 }}
          >
            {animales.length === 1 ? '1 animal' : `${animales.length} animales`}
          </Text>

          {animales.length === 0 ? (
            // No es un 404: la seccion existe y esta prendida, lo que no hay es
            // animales cargados todavia. Decirlo es mejor que una grilla vacia.
            <Empty
              description="Todavía no hay animales publicados en esta sección."
              style={{ padding: '64px 0' }}
            />
          ) : (
            <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
              {animales.map((animal) => (
                <Col xs={12} md={8} lg={6} key={animal.id_animal}>
                  {/* LA MISMA TARJETA DE LA PORTADA. El que toca "ver todos"
                      tiene que reconocer lo que venia viendo, no encontrarse
                      con otra tarjeta. */}
                  <AnimalCard
                    animal={animal}
                    config={config}
                    tipografia={tipografia}
                    slug={slug}
                  />
                </Col>
              ))}
            </Row>
          )}
        </div>
      </div>

      {conPie && (
        <SiteFooter
          config={config}
          tipografia={tipografia}
          evento={comoEvento}
          categorias={[]}
          logoUrl={logoUrl}
          slug={slug}
          navPropia={navSecciones}
          rutaInicio={`/genetica/${slug}`}
        />
      )}
    </div>
  );
};

export default SeccionCatalogoPage;
